import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle, ArrowRight, RefreshCw } from 'lucide-react';
import { walletService } from '../services/walletService';
import {
    readPendingOnlineTopUp,
    clearPendingOnlineTopUp,
    type PendingOnlineTopUp,
} from '../utils/onlineTopUp';
import { formatEuro } from '../utils/currency';

/**
 * Where Redsys returns the browser after an online recharge. `status` distinguishes the
 * two configured return routes: `ok` (UrlOK) and `ko` (UrlKO / cancel). Critically, the
 * balance is credited by the backend's server-to-server notification, NOT this browser
 * return — so on `ok` we poll the saldo until the credit lands rather than trusting the
 * redirect. On `ko` nothing was charged and nothing is credited.
 */
type ReturnStatus = 'ok' | 'ko';

type OkPhase = 'processing' | 'credited' | 'timeout' | 'unknown';

// Poll the Purse until the S2S notification credits it — the notification may land a
// moment after the User is redirected back.
const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 30000;

export default function TopUpReturnPage({ status }: { status: ReturnStatus }) {
    const navigate = useNavigate();

    // Read the in-flight recharge once on mount; the return route carries no context itself.
    // Held in state (lazy init) so it's safe to read during render and stays stable across
    // re-renders as the poll updates `phase`.
    const [pending] = useState<PendingOnlineTopUp | null>(() => readPendingOnlineTopUp());

    const [phase, setPhase] = useState<OkPhase>(
        status === 'ko' ? 'unknown' : pending ? 'processing' : 'unknown'
    );
    const [creditedTotal, setCreditedTotal] = useState<number | null>(null);

    const goToWallet = useCallback(() => {
        if (pending?.brandId) {
            navigate(`/wallet/${pending.brandId}`, { replace: true });
        } else {
            navigate('/wallet', { replace: true });
        }
    }, [navigate, pending]);

    // Failed / cancelled: nothing was charged, so nothing is credited. Just clean up.
    useEffect(() => {
        if (status === 'ko') {
            clearPendingOnlineTopUp();
        }
    }, [status]);

    // Success return: poll the saldo until it reflects the credit (or we give up waiting).
    useEffect(() => {
        if (status !== 'ok' || !pending) return;

        let cancelled = false;
        const deadline = Date.now() + POLL_TIMEOUT_MS;
        let timer: ReturnType<typeof setTimeout>;

        const poll = async () => {
            try {
                const purse = await walletService.getPurseBalance(pending.brandId);
                if (cancelled) return;

                // The credit (real + bonus) pushes the total above the pre-payment baseline.
                if (purse.totalBalance > pending.baselineTotal) {
                    setCreditedTotal(purse.totalBalance);
                    setPhase('credited');
                    clearPendingOnlineTopUp();
                    return;
                }
            } catch {
                // Transient read error — keep polling until the deadline.
            }

            if (cancelled) return;
            if (Date.now() >= deadline) {
                // The notification can still land later; leave the pending record so the
                // saldo simply appears updated next time the User opens the wallet.
                setPhase('timeout');
                return;
            }
            timer = setTimeout(poll, POLL_INTERVAL_MS);
        };

        poll();

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [status, pending]);

    return (
        <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center bg-white px-6 text-center">
            {status === 'ko' ? (
                <>
                    <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-red-50">
                        <XCircle size={44} className="text-red-500" />
                    </div>
                    <h1 className="mb-2 text-2xl font-bold text-gray-900">Recarga no completada</h1>
                    <p className="mb-8 max-w-xs text-sm leading-relaxed text-gray-500">
                        El pago se canceló o no se pudo completar, así que no se ha realizado ningún
                        cargo ni se ha añadido saldo. Puedes intentarlo de nuevo cuando quieras.
                    </p>
                </>
            ) : phase === 'processing' ? (
                <>
                    <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50">
                        <Loader2 size={44} className="animate-spin text-emerald-500" />
                    </div>
                    <h1 className="mb-2 text-2xl font-bold text-gray-900">Procesando recarga…</h1>
                    <p className="mb-8 max-w-xs text-sm leading-relaxed text-gray-500">
                        Estamos confirmando tu pago con el banco. Tu saldo se actualizará en unos
                        segundos, no cierres esta pantalla.
                    </p>
                </>
            ) : phase === 'credited' ? (
                <>
                    <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50 animate-in fade-in zoom-in">
                        <CheckCircle2 size={44} className="text-emerald-500" />
                    </div>
                    <h1 className="mb-2 text-2xl font-bold text-gray-900">¡Recarga completada!</h1>
                    {pending && (
                        <p className="mb-1 text-3xl font-black text-emerald-600">
                            +{formatEuro(pending.amount)}
                        </p>
                    )}
                    {creditedTotal !== null && (
                        <p className="mb-8 text-sm text-gray-500">
                            Nuevo saldo: <span className="font-semibold text-gray-700">{formatEuro(creditedTotal)}</span>
                        </p>
                    )}
                </>
            ) : phase === 'timeout' ? (
                <>
                    <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-amber-50">
                        <RefreshCw size={40} className="text-amber-500" />
                    </div>
                    <h1 className="mb-2 text-2xl font-bold text-gray-900">Recarga en proceso</h1>
                    <p className="mb-8 max-w-xs text-sm leading-relaxed text-gray-500">
                        Tu pago se ha enviado y estamos esperando la confirmación del banco. Tu saldo
                        se actualizará automáticamente en cuanto se acredite; puede tardar unos
                        instantes.
                    </p>
                </>
            ) : (
                <>
                    <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50">
                        <CheckCircle2 size={44} className="text-emerald-500" />
                    </div>
                    <h1 className="mb-2 text-2xl font-bold text-gray-900">Pago procesado</h1>
                    <p className="mb-8 max-w-xs text-sm leading-relaxed text-gray-500">
                        Revisa tu saldo en la cartera; si has completado el pago, se acreditará en
                        cuanto lo confirme el banco.
                    </p>
                </>
            )}

            <button
                onClick={goToWallet}
                className="flex w-full max-w-xs items-center justify-center gap-2 rounded-2xl bg-indigo-600 py-4 font-bold text-white shadow-lg shadow-indigo-100 transition-all active:scale-[0.98]"
            >
                Ver mi saldo
                <ArrowRight size={18} />
            </button>
        </div>
    );
}
